import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useAuthStore } from '@/store/auth'
import {
  getSituation,
  getFreeActivitiesForSituation,
  getPremiumActivitiesForSituation,
  type Situation,
  type Activity,
} from '@/services/content'
import Button from '@/components/ui/Button'
import Icon from '@/components/ui/Icon'
import styles from './SituationDetail.module.css'

export default function SituationDetail() {
  const { situationId } = useParams<{ situationId: string }>()
  const navigate = useNavigate()
  const user = useAuthStore((state) => state.user)
  const [situation, setSituation] = useState<Situation | null>(null)
  const [freeActivities, setFreeActivities] = useState<Activity[]>([])
  const [premiumActivities, setPremiumActivities] = useState<Activity[]>([])
  const [loading, setLoading] = useState(true)

  // Check if user has active subscription
  const hasActiveSubscription =
    user?.subscription?.status === 'trial' ||
    user?.subscription?.status === 'active'

  useEffect(() => {
    if (!situationId) return

    Promise.all([
      getSituation(situationId),
      getFreeActivitiesForSituation(situationId),
      getPremiumActivitiesForSituation(situationId),
    ])
      .then(([sit, free, premium]) => {
        setSituation(sit || null)
        setFreeActivities(free)
        setPremiumActivities(premium)
      })
      .finally(() => setLoading(false))
  }, [situationId])

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>Loading...</div>
      </div>
    )
  }

  if (!situation) {
    return (
      <div className={styles.container}>
        <div className={styles.error}>Situation not found</div>
        <Button onClick={() => navigate('/')}>Go Home</Button>
      </div>
    )
  }

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <button className={styles.backButton} onClick={() => navigate(-1)}>
          ← Back
        </button>
      </header>

      <section className={styles.hero}>
        <span className={styles.icon}><Icon name={situation.icon} size={32} /></span>
        <h1 className={styles.title}>{situation.title}</h1>
        <p className={styles.description}>{situation.description}</p>
      </section>

      <section className={styles.explanation}>
        <h2 className={styles.explanationHeadline}>
          {situation.sensoryExplanation.headline}
        </h2>
        <p className={styles.explanationBody}>
          {situation.sensoryExplanation.body}
        </p>
        <div className={styles.tip}>
          <span className={styles.tipIcon}><Icon name="lightbulb" size={22} /></span>
          <p className={styles.tipText}>{situation.sensoryExplanation.tip}</p>
        </div>
        <button
          className={styles.shareExplainer}
          onClick={() => {
            const text = `I found this helpful explanation about why our child ${situation.title.toLowerCase()}:\n\n*${situation.sensoryExplanation.headline}*\n\n${situation.sensoryExplanation.body}\n\n ${situation.sensoryExplanation.tip}\n\n— From MelloMap (parenting app)`
            const url = `https://wa.me/?text=${encodeURIComponent(text)}`
            window.open(url, '_blank')
          }}
        >
           Share this with family
        </button>
      </section>

      <section className={styles.activities}>
        <h2 className={styles.sectionTitle}>Try these activities</h2>

        {freeActivities.length > 0 ? (
          <div className={styles.activityList}>
            {freeActivities.map((activity) => (
              <button
                key={activity.id}
                className={styles.activityCard}
                onClick={() => navigate(`/activity/${activity.id}`)}
              >
                <div className={styles.activityInfo}>
                  <h3 className={styles.activityTitle}>{activity.title}</h3>
                  <p className={styles.activityMeta}>
                    {activity.duration} min • {activity.props.length === 0 ? 'No props' : activity.props.join(', ')}
                  </p>
                </div>
                <span className={styles.arrow}>→</span>
              </button>
            ))}
          </div>
        ) : (
          <p className={styles.noActivities}>No activities found for this situation</p>
        )}

        {premiumActivities.length > 0 && hasActiveSubscription && (
          <div className={styles.activityList}>
            {premiumActivities.map((activity) => (
              <button
                key={activity.id}
                className={styles.activityCard}
                onClick={() => navigate(`/activity/${activity.id}`)}
              >
                <div className={styles.activityInfo}>
                  <h3 className={styles.activityTitle}>{activity.title}</h3>
                  <p className={styles.activityMeta}>
                    {activity.duration} min • {activity.props.length === 0 ? 'No props' : activity.props.join(', ')}
                  </p>
                </div>
                <span className={styles.arrow}>→</span>
              </button>
            ))}
          </div>
        )}

        {premiumActivities.length > 0 && !hasActiveSubscription && (
          <div className={styles.premiumSection}>
            <div className={styles.premiumHeader}>
              <span className={styles.premiumBadge}>Premium</span>
              <h3 className={styles.premiumTitle}>
                {premiumActivities.length} more activities
              </h3>
            </div>
            <div className={styles.premiumPreview}>
              {premiumActivities.slice(0, 2).map((activity) => (
                <div key={activity.id} className={styles.premiumCard}>
                  <h4 className={styles.premiumActivityTitle}>{activity.title}</h4>
                  <p className={styles.premiumActivityMeta}>{activity.duration} min</p>
                </div>
              ))}
            </div>
            <Button
              variant="primary"
              fullWidth
              onClick={() => navigate('/subscription')}
            >
              Start Free Trial
            </Button>
          </div>
        )}
      </section>
    </div>
  )
}
